import { CheckCircle2, Loader2, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  ResumeCard,
  ResumeCardHeader,
} from '@/components/commercial/proposals/shared/summary-components';
import { ContractRecipientCard } from './contract-recipient-card';
import { type ContractViewData, secondsUntilContractResend } from './contract-state';
import { ContractStatusBadge } from './contract-status-badge';

interface ContractSentStateProps {
  contract: ContractViewData;
  isResending?: boolean;
  onResend?: () => void;
}

export function ContractSentState({ contract, isResending, onResend }: ContractSentStateProps) {
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    secondsUntilContractResend(contract.sentAt),
  );

  useEffect(() => {
    setSecondsRemaining(secondsUntilContractResend(contract.sentAt));
  }, [contract.sentAt]);

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const timer = window.setTimeout(
      () => setSecondsRemaining(secondsUntilContractResend(contract.sentAt)),
      1_000,
    );
    return () => window.clearTimeout(timer);
  }, [contract.sentAt, secondsRemaining]);

  return (
    <div className="space-y-5">
      <ResumeCard>
        <ResumeCardHeader
          title="Assinatura eletrônica do contrato"
          icon={<MailCheck className="h-4 w-4 text-gray-400" />}
        />
        <div className="space-y-4 px-5 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-green-50">
              <CheckCircle2 className="h-5 w-5 text-green-600" aria-hidden="true" />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold text-gray-900">Contrato enviado com sucesso</p>
              <p className="text-xs text-gray-500">
                O destinatário receberá o link de assinatura no e-mail informado abaixo.
              </p>
            </div>
          </div>
          <ContractStatusBadge status={contract.status} />
        </div>
      </ResumeCard>

      <ContractRecipientCard signerEmail={contract.signerEmail} signerName={contract.signerName} />

      <ResumeCard>
        <div className="px-5 py-4 text-center">
          {secondsRemaining > 0 ? (
            <p className="text-sm text-gray-600">
              Você poderá solicitar um novo envio em{' '}
              <span className="font-semibold tabular-nums text-gray-900">{secondsRemaining}s</span>.
            </p>
          ) : (
            <button
              type="button"
              onClick={onResend}
              disabled={isResending || !onResend}
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isResending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isResending ? 'Reenviando...' : 'Não recebi o contrato, reenviar'}
            </button>
          )}
        </div>
      </ResumeCard>
    </div>
  );
}
